/**
 * Core rendering engine for the 灯光与落地阴影编辑器 (Shadow Editor) — Ground
 * Projection model (v2 rewrite).
 *
 * Physical model: the subject stands on a horizontal ground plane. A single
 * light source, described by an azimuth (compass direction it shines FROM)
 * and a height (how high above the object it sits — NOT a second direction
 * axis), casts exactly ONE shadow of the subject's own silhouette onto that
 * ground plane, in the direction opposite the light. There is no separate
 * "contact" blob, "shape" blob and "ambient" blob layered on top of each
 * other — this file builds ONE coherent shadow shape and fades it out with
 * distance, which is what actually reads as "an object sitting on a
 * surface" instead of "an object floating in front of a wall".
 *
 * Implementation: the subject's own alpha silhouette is projected onto the
 * ground with a single 2D affine SHEAR (via `ctx.transform` + `drawImage` —
 * native, fast, anti-aliased, no manual pixel loops), pinned so the
 * subject's own contact row never moves. Points higher up the subject shift
 * further away along the "away from light" direction (exactly like how a
 * standing person's head-shadow lands further from their feet than their
 * knee-shadow does) — this alone reproduces "tight footprint at the base,
 * elongated further up" without any separate contact/shape split. A fixed
 * vertical squash keeps the whole shape hugging the ground instead of rising
 * up the frame like a wall projection. Opacity is applied AFTER geometry as
 * one continuous CanvasGradient (destination-in) along the cast axis — never
 * as a per-strip stepped globalAlpha — so the fade stays free of grey banding.
 */

import { paintBackgroundGradients, type BackgroundGradientConfig } from './backgroundPresets'

export interface CompositionParams {
  canvasW: number
  canvasH: number
  bgColor: string
  /** Composable radial/vertical/horizontal gradient axes (spec 三) — callers
   * building `CompositionParams` from persisted/legacy params are
   * responsible for running them through `migrateBackgroundGradients` first. */
  gradients: BackgroundGradientConfig
  /** Percent of the canvas long side the subject's long side occupies. */
  scalePct: number
  offsetXFrac: number
  offsetYFrac: number
}

export interface ShadowParams {
  version: 2
  /** Stable id of the `SavedShadowPreset` (see `shadowPresetLibrary.ts`)
   * this state was last loaded from/saved against, if any — purely
   * informational (shown as "来源预设：X"), never affects rendering. A
   * normal "保存" only ever writes this reference; it must NEVER cause a
   * new preset to be created (that requires the separate "另存为新预设"
   * action) and must NEVER rename/overwrite the referenced preset itself. */
  sourcePresetId?: string | null
  /** Only ever set by `migrateShadowParams` when it finds a pre-migration
   * `presetName` string with no id — a one-time hint so the caller can try
   * a best-effort name lookup in the preset library and resolve it to a
   * real `sourcePresetId` on the next save (spec 二.7/十: 兼容名称匹配，
   * 保存后应转为 sourcePresetId). Never persisted, never rendered. */
  legacyPresetName?: string
  light: {
    azimuthDeg: number
    /** 0 = light at the horizon (longest possible shadow), 100 = light
     * directly overhead (shadow tucked directly under the subject). This is
     * NOT a second direction — azimuth alone controls which way the shadow
     * points; height only controls how far it stretches. */
    heightPct: number
  }
  shadow: {
    /** 0-100, relative to the subject's own height — physically scaled by
     * light height (see `physicalLengthMultiplier`). */
    length: number
    /** 0-100, blur amount. */
    softness: number
    /** 0-100, overall darkness. */
    strength: number
    /** 0-100, how quickly the shadow fades to nothing along its length. */
    farFade: number
    /** 0-100, how much the shadow narrows as it recedes. */
    taper: number
  }
  /** Fine-tune of the ground contact line, as a fraction of the subject's
   * own bbox height. Reset to 0 by "重新识别落地点". */
  groundBaselineOffsetFrac: number
}

const WORK_LONG_SIDE = 420
const ALPHA_OCCUPIED_THRESHOLD = 8
const TRANSLUCENT_LO = 40
const TRANSLUCENT_HI = 220
const TRANSLUCENT_FRACTION_THRESHOLD = 0.35
const TRANSLUCENT_STRENGTH_FACTOR = 0.55

/** Reference elevation (degrees) at which the "阴影长度" slider's value is
 * taken at face value (multiplier = 1); lower elevations multiply it up,
 * higher elevations multiply it down. */
const LENGTH_REFERENCE_ELEVATION_DEG = 45
const LENGTH_BASE_FRAC = 0.95

export const SHADOW_INK: [number, number, number] = [40, 38, 46]

export function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v))
}

export function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas')
  c.width = Math.max(1, Math.round(w))
  c.height = Math.max(1, Math.round(h))
  return c
}

export interface SubjectAnalysis {
  naturalWidth: number
  naturalHeight: number
  /** Alpha bbox in natural-image pixel coordinates. */
  bbox: { l: number; t: number; r: number; b: number }
  translucentFraction: number
  /** bbox-cropped, ink-tinted silhouette (alpha = the subject's own alpha,
   * untouched) at work resolution — the single source shape every shadow is
   * warped from. */
  silhouette: HTMLCanvasElement
  cw: number
  ch: number
}

function alphaBBox(data: Uint8ClampedArray, w: number, h: number) {
  let l = w, t = h, r = 0, b = 0
  let found = false
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > ALPHA_OCCUPIED_THRESHOLD) {
        found = true
        if (x < l) l = x
        if (x > r) r = x
        if (y < t) t = y
        if (y > b) b = y
      }
    }
  }
  if (!found) return { l: 0, t: 0, r: w, b: h }
  return { l, t, r: r + 1, b: b + 1 }
}

/**
 * One-time analysis of the transparent PNG's alpha channel: bbox,
 * translucency fraction (for the glass/jade guard), and the cropped
 * silhouette canvas every subsequent render reuses unchanged. Never touches
 * the subject's own color pixels — only its alpha.
 */
export function analyzeSubject(img: HTMLImageElement): SubjectAnalysis {
  const naturalWidth = img.naturalWidth || img.width
  const naturalHeight = img.naturalHeight || img.height

  const workScale = Math.min(1, WORK_LONG_SIDE / Math.max(naturalWidth, naturalHeight))
  const fw = Math.max(1, Math.round(naturalWidth * workScale))
  const fh = Math.max(1, Math.round(naturalHeight * workScale))
  const full = makeCanvas(fw, fh)
  const fctx = full.getContext('2d')!
  fctx.drawImage(img, 0, 0, fw, fh)
  const fullData = fctx.getImageData(0, 0, fw, fh).data
  const workBox = alphaBBox(fullData, fw, fh)
  const bbox = {
    l: Math.round(workBox.l / workScale),
    t: Math.round(workBox.t / workScale),
    r: Math.round(workBox.r / workScale),
    b: Math.round(workBox.b / workScale),
  }
  const bw = Math.max(1, bbox.r - bbox.l)
  const bh = Math.max(1, bbox.b - bbox.t)

  const cropScale = WORK_LONG_SIDE / Math.max(bw, bh)
  const cw = Math.max(1, Math.round(bw * cropScale))
  const ch = Math.max(1, Math.round(bh * cropScale))
  const crop = makeCanvas(cw, ch)
  const cctx = crop.getContext('2d')!
  cctx.drawImage(img, bbox.l, bbox.t, bw, bh, 0, 0, cw, ch)
  const cropData = cctx.getImageData(0, 0, cw, ch).data

  let occupied = 0
  let semi = 0
  const silhouette = makeCanvas(cw, ch)
  const sctx = silhouette.getContext('2d')!
  const simg = sctx.createImageData(cw, ch)
  const [ir, ig, ib] = SHADOW_INK
  for (let i = 0; i < cw * ch; i++) {
    const a = cropData[i * 4 + 3]
    if (a > ALPHA_OCCUPIED_THRESHOLD) {
      occupied++
      if (a > TRANSLUCENT_LO && a < TRANSLUCENT_HI) semi++
    }
    simg.data[i * 4] = ir
    simg.data[i * 4 + 1] = ig
    simg.data[i * 4 + 2] = ib
    simg.data[i * 4 + 3] = a
  }
  sctx.putImageData(simg, 0, 0)
  const translucentFraction = occupied > 0 ? semi / occupied : 0

  return { naturalWidth, naturalHeight, bbox, translucentFraction, silhouette, cw, ch }
}

// ---------------------------------------------------------------------------
// Background painting — delegates entirely to the shared implementation in
// `backgroundPresets.ts` so the Shadow Editor's inherited-background preview
// can never visually diverge from what the Background Editor itself would
// render for the same params (spec 九). `comp.gradients` must already be
// migrated to the current composable scheme by the time it reaches here —
// callers building `CompositionParams` from persisted/legacy params are
// responsible for running them through `migrateBackgroundGradients` first.
// ---------------------------------------------------------------------------

export function paintBackground(ctx: CanvasRenderingContext2D, w: number, h: number, comp: CompositionParams) {
  paintBackgroundGradients(ctx, w, h, comp.bgColor, comp.gradients)
}

/** Subject's on-canvas displayed rect — identical formula to the Background
 * Editor's own placement, and must stay that way (spec: 进入阴影编辑器后不允
 * 许物体突然改变大小或位置). `renderW`/`renderH` are the actual pixel
 * dimensions of the canvas being drawn into (often a downsampled preview of
 * `comp.canvasW`/`canvasH`), so every pixel quantity here is computed
 * against them, never against the logical/output size directly. */
export function subjectDisplayRect(
  comp: CompositionParams,
  analysis: SubjectAnalysis,
  renderW: number,
  renderH: number,
) {
  const { scalePct, offsetXFrac, offsetYFrac } = comp
  const { bbox, naturalWidth, naturalHeight } = analysis
  const bboxW = Math.max(1, bbox.r - bbox.l)
  const bboxH = Math.max(1, bbox.b - bbox.t)
  const longSide = Math.max(renderW, renderH)
  const targetLong = longSide * (scalePct / 100)
  const scale = targetLong / Math.max(bboxW, bboxH)
  const dw = naturalWidth * scale
  const dh = naturalHeight * scale
  const targetCx = renderW / 2 + offsetXFrac * renderW
  const targetCy = renderH / 2 + offsetYFrac * renderH
  const left = targetCx - ((bbox.l + bbox.r) / 2) * scale
  const top = targetCy - ((bbox.t + bbox.b) / 2) * scale
  return { dw, dh, cx: left + dw / 2, cy: top + dh / 2, left, top }
}

/** Unit vector the LIGHT sits along on the ground plane, given an azimuth in
 * degrees (0 = 正前方, clockwise). The shadow is always cast in the exact
 * opposite direction. */
export function lightVector(azimuthDeg: number) {
  const rad = (azimuthDeg * Math.PI) / 180
  return { x: Math.sin(rad), y: Math.cos(rad) }
}

export function normalizeAngleRad(rad: number): number {
  const twoPi = Math.PI * 2
  let a = rad % twoPi
  if (a < 0) a += twoPi
  return a
}

/**
 * The ONLY function in this file allowed to turn `azimuthDeg` into a
 * screen-space shadow direction. It never looks at `heightPct`/elevation —
 * direction and length are two completely independent physical quantities
 * (灯光高度绝对不能改变阴影朝左还是朝右). Mathematically identical to the
 * previous inline `-lightVector(azimuthDeg)`, just named and isolated so
 * nothing else in the render path can ever accidentally re-derive azimuth
 * from a combined position/height vector.
 */
export function getShadowDirection(azimuthDeg: number) {
  const lightAzimuthRad = normalizeAngleRad((azimuthDeg * Math.PI) / 180)
  const shadowAzimuthRad = normalizeAngleRad(lightAzimuthRad + Math.PI)
  return { x: Math.sin(shadowAzimuthRad), y: Math.cos(shadowAzimuthRad), angleRad: shadowAzimuthRad }
}

/** How much the physical shadow length is multiplied relative to the
 * "阴影长度" slider's face value, purely from how high the light sits.
 * Grazing/horizon light (heightPct→0) stretches the shadow far out; light
 * directly overhead (heightPct=100) collapses it to ~0 — a real physical
 * relationship (length ∝ cot(elevation)), not an arbitrary curve. */
export function physicalLengthMultiplier(heightPct: number): number {
  const elevDeg = clamp(heightPct, 1.5, 100) / 100 * 90
  const elevRad = (elevDeg * Math.PI) / 180
  const referenceRad = (LENGTH_REFERENCE_ELEVATION_DEG * Math.PI) / 180
  const raw = Math.tan(referenceRad) / Math.tan(elevRad)
  return clamp(raw, 0.02, 6)
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}

export interface GroundShadowMask {
  canvas: HTMLCanvasElement
  anchorX: number
  anchorY: number
  outW: number
  outH: number
}

/** Baseline strip count — enough to read as continuous once blurred, far
 * cheaper than a full per-pixel remap, for a resting (near-zero-length)
 * shadow. `buildGroundShadowMask` scales the ACTUAL strip count up from
 * this baseline as `lengthPx` grows (see `effectiveStripCount` below) so
 * that neighbouring strips — each shifted by a slightly different
 * per-strip-constant amount, see `stripAffine` — never open a visible gap,
 * however long the shadow gets; because each strip's own SOURCE slice
 * shrinks proportionally as the count grows (never force-stretched to
 * bridge a gap), this scaling carries none of the noise-amplification risk
 * a rotate-and-magnify layout would have. Draft is used while the user is
 * actively dragging a slider/dial (spec 十四: 拖动过程中可以使用预览分辨率
 * 实时渲染); the final save always re-renders at `STRIP_COUNT_FULL`. */
const STRIP_COUNT_FULL = 64
const STRIP_COUNT_DRAFT = 30
/** Never subdivide more than this many times past the baseline, regardless
 * of how long the shadow gets — keeps worst-case (grazing-light) render
 * cost bounded. */
const STRIP_COUNT_MAX_MULT = 6
/** Target maximum on-screen gap (in source px) between two neighbouring
 * strips' shift amounts before the strip count is scaled up to close it. */
const GAP_TOLERANCE_FRAC = 0.012

/** Fraction of the object's own height treated as its "feet" for the
 * dedicated contact-shadow reinforcement pass (spec 九). */
const CONTACT_BAND = 0.16
/** Contact-band strips only travel this fraction of their natural
 * (heightRatio-scaled) shear distance, so the object's own feet stay
 * visually anchored even when the main cast shadow is very long (spec 九:
 * 基本不随阴影长度明显移动). */
const CONTACT_HUG = 0.22
const CONTACT_OPACITY = 0.55

/** Width narrowing (around the object's own vertical centre-line) that
 * always happens as `heightRatio` grows, even with 投影收窄程度=0. The
 * taper slider adds more on top of this floor. */
const BASE_WIDTH_NARROW = 0.24
const TAPER_WIDTH_NARROW = 0.36
const WIDTH_SCALE_MIN = 0.34

/** Vertical squash applied to every strip's own thickness, ALWAYS along the
 * silhouette's own screen-Y axis — never rotated to follow the light's
 * azimuth (see `planStripGeometry` for why that distinction is the whole
 * fix here). Represents the camera's own shallow viewing angle onto the
 * ground plane. Strips near `heightRatio`=1 (the object's own top) get
 * squashed a little extra on top of this so the shadow keeps melting into
 * the ground instead of reading as a raised ridge as it recedes (spec 八).
 */
const GROUND_SQUASH = 0.34
const FAR_EXTRA_SQUASH = 0.6
/** Grazing/low light flattens the whole silhouette further still, on top
 * of `GROUND_SQUASH` — this multiplies the squash only; it never appears
 * in `dirX`/`dirY` or in `stripAffine`'s shear terms, so raising or
 * lowering the light can only ever make the shape flatter/taller, never
 * change which way it leans (spec 八: flattenFactor 只能控制地面压缩，不能
 * 改变方位角). */
const LOW_LIGHT_EXTRA_FLATTEN = 0.55

/** `heightRatio` band over which a strip's own look transitions from "near
 * the object: sharp + dark" to "far from the object: soft + faint". */
const NEAR_FAR_BLEND = 0.4

interface StripTransform {
  a: number
  b: number
  c: number
  d: number
  e: number
  f: number
  y0: number
  y1: number
  heightRatio: number
}

/** Purely the "ground-flattening" shape of one strip — anchored at the
 * silhouette's own base and squashed toward it — with NO dependency at all
 * on the light's direction or the shadow's length. `destY1`/`scaleY`
 * describe where this strip's squashed thickness sits once every strip
 * below it (lower heightRatio) has already been stacked, so consecutive
 * strips tile with zero gap regardless of squash amount. */
interface StripGeometry {
  y0: number
  y1: number
  heightRatio: number
  widthScale: number
  scaleY: number
  destY1: number
}

/**
 * Squashes the silhouette's own vertical extent toward its base (spec 五/
 * 六: 阴影的根部必须锚定在器物底部) into a shorter stack of strips — this
 * alone is the "flattened onto the ground" deformation. Deliberately takes
 * no `dirX`/`dirY`/`lengthPx` parameter: nothing about the light's azimuth
 * or the shadow's length can leak into this shape, which is what guarantees
 * changing them can never rotate or resize this base geometry, only shift
 * it (see `stripAffine`).
 */
function planStripGeometry(
  ch: number,
  stripCount: number,
  narrowAtTop: number,
  flattenMultiplier: number,
): StripGeometry[] {
  const stripH = ch / stripCount
  const geoms: StripGeometry[] = []
  let cumulative = 0
  for (let i = 0; i < stripCount; i++) {
    const y1 = ch - i * stripH
    const y0 = Math.max(0, y1 - stripH)
    const yMid = (y0 + y1) / 2
    const heightRatio = clamp((ch - yMid) / ch, 0, 1)
    const widthScale = clamp(1 - narrowAtTop * heightRatio, WIDTH_SCALE_MIN, 1)
    const squash = GROUND_SQUASH * flattenMultiplier * lerp(1, FAR_EXTRA_SQUASH, heightRatio)
    // Only a numerical-safety epsilon, NOT a visual floor: a real minimum
    // here would make the accumulated footprint height grow with strip
    // count alone (more, thinner strips each padded up to the floor) —
    // wrong, since subdividing the same squashed footprint more finely
    // must sum back to the same total regardless of how many slices it is
    // cut into.
    const squashedThickness = Math.max(1e-2, (y1 - y0) * squash)
    const destY1 = ch - cumulative
    cumulative += squashedThickness
    geoms.push({
      y0,
      y1,
      heightRatio,
      widthScale,
      scaleY: squashedThickness / Math.max(1e-3, y1 - y0),
      destY1,
    })
  }
  return geoms
}

/**
 * Places one already-flattened strip (spec 六/七) by adding a SHEAR — never
 * a rotation — proportional to its own `heightRatio` along the shadow
 * direction. This is the single most important fix in this file: the
 * previous model rotated each strip's width/height axes onto the shadow's
 * own forward/side basis, which literally tilts the whole silhouette to
 * lean along the light's azimuth — reading as "the object's own outline
 * leaning over like a wall" for any oblique direction. A shear instead
 * keeps every strip's own content aligned with the real screen X/Y axes
 * (never rotated) and only ever TRANSLATES it (`e`/`f` only — `a`/`b`/`c`/
 * `d` come from `planStripGeometry` alone), by a per-strip CONSTANT amount
 * proportional to its own `heightRatio`; stacking many such strips is what
 * produces the classic "flattened footprint, swept away from the light"
 * cast-shadow look for every azimuth, not just near-vertical ones.
 *
 * Deliberately a per-strip CONSTANT shift, not a value that varies
 * continuously with the strip's own source Y: folding the shift into a
 * term linear in source Y would add it directly into the `c`/`d`
 * coefficients (the strip's own local scale), which would make the
 * light's height silently warp the strip's shape (stretch/compress it)
 * instead of purely translating it — exactly the "one control leaks into
 * another" bug this whole rewrite exists to eliminate. Because the shift
 * is skipped is an extra `e`/`f` term, `dirX`/`dirY` (azimuth-only, via
 * `getShadowDirection`) and `lengthPx` (height + 阴影长度-slider only) are
 * taken as two independent numbers and only ever multiplied together —
 * changing `lengthPx` can only ever change how far a strip is translated,
 * never its shape or which way it leans. `hugFrac` (<1) lets the dedicated
 * contact-shadow pass keep the object's own feet from drifting away from
 * the base even when `lengthPx` is very large (spec 九).
 */
function stripAffine(
  g: StripGeometry,
  cw: number,
  dirX: number,
  dirY: number,
  lengthPx: number,
  hugFrac: number,
): StripTransform {
  const ccx = cw / 2
  const shiftX = dirX * lengthPx * hugFrac * g.heightRatio
  const shiftY = dirY * lengthPx * hugFrac * g.heightRatio
  return {
    a: g.widthScale,
    b: 0,
    c: 0,
    d: g.scaleY,
    e: ccx * (1 - g.widthScale) + shiftX,
    f: g.destY1 - g.y1 * g.scaleY + shiftY,
    y0: g.y0,
    y1: g.y1,
    heightRatio: g.heightRatio,
  }
}

function stripCornerBounds(t: StripTransform, cw: number) {
  const pts: Array<[number, number]> = [[0, t.y0], [cw, t.y0], [0, t.y1], [cw, t.y1]]
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  for (const [x, y] of pts) {
    const px = t.a * x + t.c * y + t.e
    const py = t.b * x + t.d * y + t.f
    if (px < minX) minX = px
    if (px > maxX) maxX = px
    if (py < minY) minY = py
    if (py > maxY) maxY = py
  }
  return { minX, maxX, minY, maxY }
}

/**
 * Builds the ONE ground shadow (spec 五) as ground-flattened strips (spec
 * 六) each sheared — never rotated — toward the shadow direction (spec
 * 七). `elevationFlattenFrac` (0 = light at the horizon, 1 = light
 * overhead) ONLY multiplies the squash amount (spec 八); it never appears
 * in `dirX`/`dirY`, so no amount of light-height change can ever alter the
 * shadow's orientation — only how flat/long it is. A dedicated
 * contact-band pass (spec 九) keeps the object's own feet visually anchored
 * no matter how long the main cast shadow stretches.
 */
export function buildGroundShadowMask(
  silhouette: HTMLCanvasElement,
  cw: number,
  ch: number,
  dirX: number,
  dirY: number,
  lengthPx: number,
  taperParam: number,
  softnessFrac: number,
  farFadeParam: number,
  elevationFlattenFrac: number,
  stripCount: number = STRIP_COUNT_FULL,
): GroundShadowMask {
  const narrowAtTop = clamp(
    BASE_WIDTH_NARROW + (clamp(taperParam, 0, 100) / 100) * TAPER_WIDTH_NARROW,
    0,
    1 - WIDTH_SCALE_MIN,
  )
  const flattenMultiplier = lerp(LOW_LIGHT_EXTRA_FLATTEN, 1, clamp(elevationFlattenFrac, 0, 1))

  // Neighbouring strips are shifted by a per-strip CONSTANT amount that
  // grows with `lengthPx` (see `stripAffine`) — subdivide finely enough
  // that this per-strip shift step never exceeds a small fraction of the
  // silhouette's own size, so consecutive strips always visually overlap
  // regardless of how long the shadow gets. Each strip's own source slice
  // shrinks proportionally as the count grows (nothing is force-stretched
  // to bridge the gap), so this never reintroduces per-strip magnification
  // artifacts.
  const gapTolerancePx = Math.max(0.5, ch * GAP_TOLERANCE_FRAC)
  const effectiveStripCount = clamp(
    Math.ceil(Math.max(stripCount, lengthPx / gapTolerancePx)),
    stripCount,
    stripCount * STRIP_COUNT_MAX_MULT,
  )

  const geoms = planStripGeometry(ch, effectiveStripCount, narrowAtTop, flattenMultiplier)
  const plans = geoms.map((g) => stripAffine(g, cw, dirX, dirY, lengthPx, 1))
  const contactPlans = geoms
    .filter((g) => g.heightRatio <= CONTACT_BAND)
    .map((g) => stripAffine(g, cw, dirX, dirY, lengthPx, CONTACT_HUG))

  let nearBlur = Math.max(0.5, ch * 0.006 * (0.3 + softnessFrac * 1.1))
  // Blur still grows mainly with the shadow's OWN length (so a near-overhead
  // shadow never inherits a full-size blur halo) but never bottoms out at
  // zero, so even a tight overhead footprint keeps a soft, grounded edge.
  const growth = clamp(lengthPx / (ch * 0.18), 0.12, 1)
  let farBlur = clamp(ch * 0.05 * (0.35 + softnessFrac * 1.25) * growth + nearBlur, nearBlur, ch * 0.14)

  // Safety floor (VISTOERA_SHADOW_ZEBRA_ARTIFACT_P0) — mirrors the backend
  // canonical renderer's identical floor in `_cast_ground_shadow`
  // (`shadow_synthesis.py`). `effectiveStripCount` is deliberately capped
  // above (`STRIP_COUNT_MAX_MULT`) to bound worst-case render cost, so for a
  // long/grazing-light shadow the per-strip shift step
  // (`lengthPx / effectiveStripCount`) can exceed what `GAP_TOLERANCE_FRAC`
  // was targeting — and independently, a low `softness` slider value alone
  // can shrink `nearBlur`/`farBlur` below that step even when the strip cap
  // was never hit. Either way the untouched blur radii above are too small
  // to bridge the real per-strip gap, which is exactly what reads as a
  // regular zebra-stripe/scan-line pattern in the rendered shadow (proven
  // via `e2e/shadow-mask-periodicity.spec.ts`'s DFT band-ratio metric —
  // without this floor, a long/grazing-light/low-softness shadow measures
  // 30-280x its own healthy short/long-period power ratio; with it, well
  // under the 8x threshold). This is a real per-strip GAP being bridged by
  // blur — never a substitute for the strip-count scaling above — so it
  // must be applied AFTER, not instead of, the existing formulas.
  const worstStepPx = lengthPx / Math.max(1, effectiveStripCount)
  nearBlur = Math.max(nearBlur, worstStepPx * 1.4)
  farBlur = Math.max(farBlur, worstStepPx * 1.4)

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  for (const p of [...plans, ...contactPlans]) {
    const b = stripCornerBounds(p, cw)
    minX = Math.min(minX, b.minX)
    maxX = Math.max(maxX, b.maxX)
    minY = Math.min(minY, b.minY)
    maxY = Math.max(maxY, b.maxY)
  }
  const margin = Math.max(4, ch * 0.03) + farBlur * 1.6
  const outW = Math.max(1, Math.ceil(maxX - minX + margin * 2))
  const outH = Math.max(1, Math.ceil(maxY - minY + margin * 2))
  const anchorX = margin - minX
  const anchorY = margin - minY

  const fadePower = lerp(0.6, 3.2, clamp(farFadeParam, 0, 100) / 100)

  const drawStrip = (ctx: CanvasRenderingContext2D, p: StripTransform, alpha: number) => {
    if (alpha <= 0.002) return
    ctx.save()
    ctx.globalAlpha = clamp(alpha, 0, 1)
    ctx.translate(anchorX, anchorY)
    ctx.transform(p.a, p.b, p.c, p.d, p.e, p.f)
    const h = Math.max(1e-2, p.y1 - p.y0)
    ctx.drawImage(silhouette, 0, p.y0, cw, h, 0, p.y0, cw, h)
    ctx.restore()
  }

  // ------------------------------------------------------------------
  // Banding fix: geometry is composited at FULL opacity (no per-strip
  // envelope). The previous model assigned each strip a discrete
  // `globalAlpha = envelope(heightRatio)` — with ~64–384 strips that is
  // literally a stepped opacity staircase (10%/20%/… style), which
  // survives blur as visible grey bands. Continuous fade is applied once
  // afterwards via a CanvasGradient + destination-in (float alpha stops,
  // no putImageData / Uint8 threshold).
  // ------------------------------------------------------------------
  const castLayer = makeCanvas(outW, outH)
  const castCtx = castLayer.getContext('2d')!
  for (const p of plans) drawStrip(castCtx, p, 1)

  // Contact point in mask space = local crop bottom-centre after the
  // shared translate(anchorX, anchorY) applied inside drawStrip.
  const originX = anchorX + cw / 2
  const originY = anchorY + ch
  const tipLen = Math.max(1, lengthPx)
  const tipX = originX + dirX * tipLen
  const tipY = originY + dirY * tipLen

  const fadeMask = makeCanvas(outW, outH)
  const fadeCtx = fadeMask.getContext('2d')!
  const grad = fadeCtx.createLinearGradient(originX, originY, tipX, tipY)
  // Dense power-curve stops so the gradient itself is continuous, not a
  // handful of flat plateaus. Canvas interpolates between stops in float
  // space before writing 8-bit pixels — that is enough to eliminate the
  // strip-step banding (the remaining 8-bit quantisation is sub-pixel
  // noise after blur, not structured bands).
  const FADE_STOPS = 48
  for (let i = 0; i <= FADE_STOPS; i++) {
    const t = i / FADE_STOPS
    const a = Math.pow(1 - t, fadePower)
    grad.addColorStop(t, `rgba(0,0,0,${a})`)
  }
  fadeCtx.fillStyle = grad
  fadeCtx.fillRect(0, 0, outW, outH)
  castCtx.globalCompositeOperation = 'destination-in'
  castCtx.drawImage(fadeMask, 0, 0)
  castCtx.globalCompositeOperation = 'source-over'

  // Contact reinforcement: constant opacity (no heightRatio staircase) so
  // it cannot reintroduce bands; short hug geometry only.
  const contactLayer = makeCanvas(outW, outH)
  const contactCtx = contactLayer.getContext('2d')!
  for (const p of contactPlans) drawStrip(contactCtx, p, CONTACT_OPACITY)

  // Softness: one continuous CSS blur per layer (Canvas filter, not a
  // re-quantised putImageData path). Near blur on contact+cast, then an
  // extra far blur of the cast only, weighted by a continuous gradient
  // along the same axis — never by discrete strip alphas.
  const nearSoft = makeCanvas(outW, outH)
  const nearSoftCtx = nearSoft.getContext('2d')!
  nearSoftCtx.filter = `blur(${nearBlur}px)`
  nearSoftCtx.drawImage(castLayer, 0, 0)
  nearSoftCtx.drawImage(contactLayer, 0, 0)
  nearSoftCtx.filter = 'none'

  const farSoft = makeCanvas(outW, outH)
  const farSoftCtx = farSoft.getContext('2d')!
  farSoftCtx.filter = `blur(${farBlur}px)`
  farSoftCtx.drawImage(castLayer, 0, 0)
  farSoftCtx.filter = 'none'

  // Continuous near→far blend weight (same gradient axis as the fade).
  const blendMask = makeCanvas(outW, outH)
  const blendCtx = blendMask.getContext('2d')!
  const blendGrad = blendCtx.createLinearGradient(originX, originY, tipX, tipY)
  const BLEND_STOPS = 32
  for (let i = 0; i <= BLEND_STOPS; i++) {
    const t = i / BLEND_STOPS
    // Near the object: prefer the sharper nearSoft; far tip: prefer farSoft.
    const farWeight = clamp((t - NEAR_FAR_BLEND * 0.25) / Math.max(1e-3, 1 - NEAR_FAR_BLEND * 0.25), 0, 1)
    blendGrad.addColorStop(t, `rgba(0,0,0,${farWeight})`)
  }
  blendCtx.fillStyle = blendGrad
  blendCtx.fillRect(0, 0, outW, outH)

  const farOnly = makeCanvas(outW, outH)
  const farOnlyCtx = farOnly.getContext('2d')!
  farOnlyCtx.drawImage(farSoft, 0, 0)
  farOnlyCtx.globalCompositeOperation = 'destination-in'
  farOnlyCtx.drawImage(blendMask, 0, 0)
  farOnlyCtx.globalCompositeOperation = 'source-over'

  const mask = makeCanvas(outW, outH)
  const mctx = mask.getContext('2d')!
  mctx.drawImage(nearSoft, 0, 0)
  mctx.drawImage(farOnly, 0, 0)

  return { canvas: mask, anchorX, anchorY, outW, outH }
}

export interface RenderOptions {
  quality?: 'full' | 'draft'
}

/**
 * Paints background → single ground shadow → subject onto `ctx`, sized
 * `targetW x targetH`. No editor chrome (dial, guides, baseline marker) is
 * ever drawn here — callers overlay those as separate DOM elements so they
 * can never leak into a `canvas.toBlob()` export.
 */
export function renderComposite(
  ctx: CanvasRenderingContext2D,
  targetW: number,
  targetH: number,
  subjectImg: HTMLImageElement,
  analysis: SubjectAnalysis,
  comp: CompositionParams,
  shadow: ShadowParams,
  opts: RenderOptions = {},
) {
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  ctx.globalAlpha = 1
  ctx.filter = 'none'
  paintBackground(ctx, targetW, targetH, comp)

  const { dw, dh, cx, cy } = subjectDisplayRect(comp, analysis, targetW, targetH)
  const { bbox, naturalWidth, naturalHeight, silhouette, cw, ch } = analysis
  const bboxDW = ((bbox.r - bbox.l) / naturalWidth) * dw
  const bboxDH = ((bbox.b - bbox.t) / naturalHeight) * dh
  const bboxCX = cx - dw / 2 + (((bbox.l + bbox.r) / 2 / naturalWidth) * dw)
  const baselineY = cy - dh / 2 + (bbox.b / naturalHeight) * dh + shadow.groundBaselineOffsetFrac * bboxDH

  // Direction depends ONLY on azimuth (spec 二); length depends ONLY on
  // height + the 阴影长度 slider (spec 三/四). Neither computation ever
  // reads from the other, by construction — this is what guarantees
  // dragging "灯光高度" can never flip the shadow left/right.
  const shadowDir = getShadowDirection(shadow.light.azimuthDeg)
  const physMult = physicalLengthMultiplier(shadow.light.heightPct)
  const lengthFrac = clamp(shadow.shadow.length, 0, 100) / 100
  const lengthPxCrop = lengthFrac * LENGTH_BASE_FRAC * ch * physMult
  const softnessFrac = clamp(shadow.shadow.softness, 0, 100) / 100
  const strengthFrac = clamp(shadow.shadow.strength, 0, 100) / 100
  const translucentGuard = analysis.translucentFraction >= TRANSLUCENT_FRACTION_THRESHOLD ? TRANSLUCENT_STRENGTH_FACTOR : 1

  const stripCount = opts.quality === 'draft' ? STRIP_COUNT_DRAFT : STRIP_COUNT_FULL
  const { canvas: maskCanvas, anchorX, anchorY, outW, outH } = buildGroundShadowMask(
    silhouette,
    cw,
    ch,
    shadowDir.x,
    shadowDir.y,
    lengthPxCrop,
    shadow.shadow.taper,
    softnessFrac,
    shadow.shadow.farFade,
    clamp(shadow.light.heightPct, 0, 100) / 100,
    stripCount,
  )

  // `anchorX`/`anchorY` only describe where the mask's own bounding-box
  // minimum sits inside the (margin-padded) mask canvas — they say nothing
  // about the object's ground-contact point. In the mask's local coordinate
  // space (the same space every strip's `y0`/`y1` and `stripAffine` shift
  // live in), the object's OWN contact row is anchored at local
  // `(cw / 2, ch)` — bottom-center of the bbox-crop — because
  // `planStripGeometry`'s heightRatio-0 strip keeps `destY1 = ch` and an
  // unscaled/unshifted X range of `[0, cw]` regardless of shadow length or
  // squash (see `planStripGeometry`/`stripAffine`). That local point is what
  // must land on screen at `(bboxCX, baselineY)`; forgetting the `cw/2`/`ch`
  // offset here previously left the whole mask translated down by `ch *
  // scaleY` (~ the object's own crop height in screen px) and right by
  // `cw/2 * scaleX`, which is what made long shadows render entirely below
  // the visible canvas instead of hugging the subject's own feet.
  const scaleX = bboxDW / cw
  const scaleY = bboxDH / ch
  const destW = outW * scaleX
  const destH = outH * scaleY
  const destX = bboxCX - (cw / 2) * scaleX - anchorX * scaleX
  const destY = baselineY - ch * scaleY - anchorY * scaleY

  ctx.save()
  ctx.globalCompositeOperation = 'multiply'
  ctx.globalAlpha = clamp(strengthFrac * 0.85, 0, 0.85) * translucentGuard
  ctx.drawImage(maskCanvas, destX, destY, destW, destH)
  ctx.restore()

  // Subject on top, untouched.
  ctx.save()
  ctx.globalCompositeOperation = 'source-over'
  ctx.globalAlpha = 1
  ctx.filter = 'none'
  ctx.drawImage(subjectImg, cx - dw / 2, cy - dh / 2, dw, dh)
  ctx.restore()

  ctx.restore()
}

// ---------------------------------------------------------------------------
// Defaults + backward-compat migration from the old (v1, multi-layer) schema.
// ---------------------------------------------------------------------------

export const DEFAULT_SHADOW_PARAMS: ShadowParams = {
  version: 2,
  light: { azimuthDeg: 315, heightPct: 55 },
  shadow: { length: 38, softness: 60, strength: 45, farFade: 55, taper: 30 },
  groundBaselineOffsetFrac: 0,
}

/** Best-effort migration for chains whose `shadow_params_json` was written
 * by the pre-rewrite multi-layer (contact/shape/ambient) editor — maps what
 * it can onto the new schema instead of silently discarding the user's
 * previous light direction. Anything unrecognized falls back to defaults. */
export function migrateShadowParams(raw: unknown): ShadowParams {
  if (!raw || typeof raw !== 'object') return JSON.parse(JSON.stringify(DEFAULT_SHADOW_PARAMS))
  const r = raw as Record<string, any>
  if (r.version === 2 && r.light && r.shadow) {
    return {
      version: 2,
      sourcePresetId: typeof r.sourcePresetId === 'string' ? r.sourcePresetId : null,
      // Legacy raw field kept only so callers can attempt a best-effort
      // name-based preset lookup for data saved before `sourcePresetId`
      // existed — never written by fresh saves.
      ...(typeof r.presetName === 'string' && !r.sourcePresetId ? { legacyPresetName: r.presetName } : {}),
      light: {
        azimuthDeg: Number(r.light.azimuthDeg) || 0,
        heightPct: clamp(Number(r.light.heightPct), 0, 100) || DEFAULT_SHADOW_PARAMS.light.heightPct,
      },
      shadow: {
        length: clamp(Number(r.shadow.length), 0, 100),
        softness: clamp(Number(r.shadow.softness), 0, 100),
        strength: clamp(Number(r.shadow.strength), 0, 100),
        farFade: clamp(Number(r.shadow.farFade), 0, 100),
        taper: clamp(Number(r.shadow.taper), 0, 100),
      },
      groundBaselineOffsetFrac: Number(r.groundBaselineOffsetFrac) || 0,
    }
  }
  // v1 shape: { light: {azimuthDeg, elevationMode}, shadow: {length, softness, strength, farFade, projectionCompression}, contactShadow, ambientShadow }
  if (r.light && r.shadow) {
    const azimuthDeg = Number(r.light.azimuthDeg) || 0
    const heightPct = r.light.elevationMode === 'overhead' ? 100 : 50
    return {
      version: 2,
      light: { azimuthDeg, heightPct },
      shadow: {
        length: clamp(Number(r.shadow.length), 0, 100) || DEFAULT_SHADOW_PARAMS.shadow.length,
        softness: clamp(Number(r.shadow.softness), 0, 100) || DEFAULT_SHADOW_PARAMS.shadow.softness,
        strength: clamp(Number(r.shadow.strength), 0, 100) || DEFAULT_SHADOW_PARAMS.shadow.strength,
        farFade: clamp(Number(r.shadow.farFade), 0, 100) || DEFAULT_SHADOW_PARAMS.shadow.farFade,
        taper: clamp(Number(r.shadow.projectionCompression), 0, 100) || DEFAULT_SHADOW_PARAMS.shadow.taper,
      },
      groundBaselineOffsetFrac: Number(r.contactShadow?.baselineOffsetFrac) || 0,
    }
  }
  return JSON.parse(JSON.stringify(DEFAULT_SHADOW_PARAMS))
}
